import React, { useState, useEffect, createContext } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './App.css';
import { auth, onAuthStateChanged } from './firebase';
import { EmailProvider } from './contexts/EmailContext';
import { StripeProvider } from './contexts/StripeContext';
import { AuthProvider } from './contexts/AuthContext';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { Analytics } from '@vercel/analytics/react';
import analytics from './services/AnalyticsService';
import GmailCallback from './components/GmailCallback';
// import AnalyticsTestButton from './components/AnalyticsTestButton';
import AppLayout from './components/AppLayout';
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
// import ProjectDashboard from './pages/Projects/ProjectDashboard';
import UnifiedProjectsDashboard from './pages/Projects/UnifiedProjectsDashboard';
import ProjectDetails from './pages/Projects/ProjectDetails';
import Clients from './components/Clients';
import ProfileView from './pages/Profile/ProfileView';
import ProfileEditor from './pages/Profile/ProfileEditor';
import WelcomeProfile from './pages/Profile/WelcomeProfile';
import OnboardingFlow from './pages/Onboarding/OnboardingFlow';
import Agreement from './pages/Agreement';
import ErrorBoundary from './components/ErrorBoundary';
import PreviewInvoice from './pages/Invoice/PreviewInvoice';
import EditInvoice from './pages/Invoice/EditInvoice';
import PaymentPage from './pages/Invoice/PaymentPage';
import PaymentConfirmation from './pages/Invoice/PaymentConfirmation';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import SubscriptionGuard from './components/SubscriptionGuard';
import SubscriptionPage from './pages/SubscriptionPage';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SurveyResponse from './components/SurveyResponse';
import SurveyResponses from './pages/SurveyResponses';
import SurveyThankYou from './pages/SurveyThankYou';
import SurveySection from './components/SurveySection';
import TeamPage from './pages/Team/TeamPage';
import PipelinePage from './pages/Pipeline/PipelinePage';
import BookingPage from './pages/Booking/BookingPage';
import AnalyticsPage from './pages/Analytics/AnalyticsPage';
import PublicBookingPage from './pages/Booking/PublicBookingPage';
import CreateAgreementPage from './pages/Agreement/CreateAgreementPage';
import { LeadsDashboard, FormBuilder, LeadDetail } from './pages/Leads';
import { PublicIntakeForm } from './pages/IntakeForm';
import RiskDashboard from './pages/Admin/RiskDashboard';
import SettingsPage from './pages/SettingsPage';
// import SubscriptionStatusDebug from './components/SubscriptionStatusDebug';
// import PaymentEmailTest from './components/PaymentEmailTest';

export const ThemeContext = createContext();
export const MfaContext = createContext();

// eslint-disable-next-line no-unused-vars
const ThemeToggle = () => {
  const { theme, setTheme } = React.useContext(ThemeContext);

  const handleThemeChange = () => {
    if (theme === 'dark') {
      setTheme('light');
    } else if (theme === 'light') {
      setTheme('system');
    } else {
      setTheme('dark');
    }
  };

  return (
    <button
      onClick={handleThemeChange}
      className="fixed bottom-4 left-4 z-50 bg-white/30 dark:bg-black/40 backdrop-blur-lg p-2.5 rounded-xl text-sm font-medium flex items-center justify-center transition-all hover:bg-white/40 dark:hover:bg-white/20 border border-gray-200/20 dark:border-white/10 shadow-lg"
      title={`Current theme: ${theme}`}
    >
      {theme === 'dark' && (
        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      )}
      {theme === 'light' && (
        <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
        </svg>
      )}
      {theme === 'system' && (
        <svg className="w-5 h-5 text-gray-900 dark:text-white" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
        </svg>
      )}
    </button>
  );
};

const ProtectedRoute = ({ children, currentUser }) => {
  if (!currentUser) {
    return <Navigate to="/" />;
  }
  
  // Only check for onboarding if explicitly set from account creation flows
  // This prevents onboarding from showing up for returning users
  const isNewUser = sessionStorage.getItem('isNewUser') === 'true';
  const isOnboardingPath = window.location.pathname.includes('/app/onboarding');
  
  // If they're already on the onboarding path, let them proceed
  if (isOnboardingPath) {
    return children;
  }
  
  // If they're a new user but not on the onboarding path, redirect them
  // Only do this if they are truly a new user from account creation
  if (isNewUser && !isOnboardingPath) {
    return <Navigate to="/app/onboarding" />;
  }
  
  // Regular authenticated user, let them access the route
  return children;
};

const PublicRoute = ({ children, currentUser }) => {
  if (currentUser) {
    // Only redirect to onboarding if they are a new user
    const isNewUser = sessionStorage.getItem('isNewUser') === 'true';
    if (isNewUser) {
      return <Navigate to="/app/onboarding" />;
    }
    // Otherwise redirect to the main app
    return <Navigate to="/app" />;
  }
  return children;
};

const RootLayout = ({ children, currentUser }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        return savedTheme;
      }
      return 'system';
    }
    return 'system';
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleThemeChange = () => {
      if (theme === 'system') {
        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(mediaQuery.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleThemeChange);
    
    // Initial setup
    if (theme === 'system') {
      const root = window.document.documentElement;
      root.classList.remove('light', 'dark');
      root.classList.add(mediaQuery.matches ? 'dark' : 'light');
    } else {
      const root = window.document.documentElement;
      root.classList.remove('light', 'dark');
      root.classList.add(theme);
    }

    localStorage.setItem('theme', theme);

    return () => {
      mediaQuery.removeEventListener('change', handleThemeChange);
    };
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      <AuthProvider currentUser={currentUser}>
        <WorkspaceProvider>
          <EmailProvider user={currentUser}>
            <StripeProvider user={currentUser}>
              {children}
              {/* <ThemeToggle /> - moved to settings */}
              {/* <AnalyticsTestButton /> */}
              {/* <SubscriptionStatusDebug /> */}
              <Toaster position="top-right" />
              <Analytics />
            </StripeProvider>
          </EmailProvider>
        </WorkspaceProvider>
      </AuthProvider>
    </ThemeContext.Provider>
  );
};

const OnboardingModal = () => {
  const [isOpen, setIsOpen] = useState(true);
  const navigate = useNavigate();

  // Check if the user is actually a new user or just redirected here
  useEffect(() => {
    const isNewUser = sessionStorage.getItem('isNewUser') === 'true';
    if (!isNewUser) {
      // If not a new user, redirect them to dashboard
      navigate('/app');
    }
  }, [navigate]);

  const handleClose = () => {
    setIsOpen(false);
    navigate('/app');
  };

  return <OnboardingFlow isOpen={isOpen} onClose={handleClose} />;
};

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Add a global error handler for uncaught errors
  useEffect(() => {
    const handleError = (event) => {
      console.error('Global error:', event.error || event.message);
      // Prevent the default browser error handling
      event.preventDefault();
      
      // If error is due to navigation or state update after unmount, no need to show error
      if (event.error && (
        event.error.message?.includes('unmounted component') || 
        event.error.message?.includes('navigation')
      )) {
        console.log('Suppressing navigation-related error');
        return;
      }
    };
    
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleError);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleError);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
      
      // Identify user in analytics when authentication state changes
      if (user) {
        analytics.identify(user.uid, {
          email: user.email,
          name: user.displayName,
          email_verified: user.emailVerified,
          provider: user.providerData[0]?.providerId,
          created_at: user.metadata.creationTime,
          last_sign_in: user.metadata.lastSignInTime,
          user_type: 'photographer'
        });
      } else {
        // Reset analytics when user logs out
        analytics.reset();
      }
    });
    return () => unsubscribe();
  }, []);

  const router = createBrowserRouter([
    {
      path: '/',
      element: (
        <RootLayout currentUser={currentUser}>
          <PublicRoute currentUser={currentUser}>
            <LandingPage />
          </PublicRoute>
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/terms',
      element: (
        <RootLayout currentUser={currentUser}>
          <TermsOfService />
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/privacy',
      element: (
        <RootLayout currentUser={currentUser}>
          <PrivacyPolicy />
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/profile/:username',
      element: (
        <RootLayout currentUser={currentUser}>
          <ProfileView />
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/app',
      element: (
        <RootLayout currentUser={currentUser}>
          <ProtectedRoute currentUser={currentUser}>
            <SubscriptionGuard>
              <AppLayout />
            </SubscriptionGuard>
          </ProtectedRoute>
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />,
      children: [
        {
          path: 'subscribe',
          element: <SubscriptionPage />
        },
        {
          path: 'projects',
          children: [
            {
              index: true,
              element: <UnifiedProjectsDashboard />
            },
            {
              path: ':projectId',
              element: <ProjectDetails />
            }
          ]
        },
        {
          path: 'agreements',
          children: [
            {
              index: true,
              element: <div>Agreements Dashboard - Coming Soon</div>
            },
            {
              path: 'create',
              element: <CreateAgreementPage />
            }
          ]
        },
        {
          path: 'surveys',
          element: <SurveySection user={currentUser} />
        },
        {
          path: 'clients',
          element: <Clients />
        },
        {
          path: 'team',
          element: <TeamPage />
        },
        {
          path: 'pipeline',
          element: <PipelinePage />
        },
        {
          path: 'leads',
          children: [
            {
              index: true,
              element: <LeadsDashboard />
            },
            {
              path: 'forms/new',
              element: <FormBuilder />
            },
            {
              path: 'forms/:formId',
              element: <FormBuilder />
            },
            {
              path: ':leadId',
              element: <LeadDetail />
            }
          ]
        },
        {
          path: 'booking',
          element: <BookingPage />
        },
        {
          path: 'analytics',
          element: <AnalyticsPage />
        },
        {
          path: 'admin/risk',
          element: <RiskDashboard />
        },
        {
          path: 'settings',
          element: <SettingsPage />
        },
        {
          path: 'profile',
          element: <ProfileView />
        },
        {
          path: 'profile/edit',
          element: <ProfileEditor />
        },
        {
          path: 'profile/welcome',
          element: <WelcomeProfile />
        },
        {
          path: 'onboarding',
          element: <OnboardingModal />
        },
        // {
        //   path: 'payment-email-test',
        //   element: <PaymentEmailTest />
        // },
        {
          index: true,
          element: sessionStorage.getItem('isNewUser') === 'true' ?
                  <Navigate to="/app/onboarding" replace /> :
                  <DashboardPage />
        }
      ]
    },
    {
      path: '/book/:slug',
      element: (
        <RootLayout currentUser={currentUser}>
          <PublicBookingPage />
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/intake/:slug',
      element: (
        <RootLayout currentUser={currentUser}>
          <PublicIntakeForm />
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/agreement/:agreementId',
      element: (
        <RootLayout currentUser={currentUser}>
          <Agreement />
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/agreements/:agreementId',
      element: (
        <RootLayout currentUser={currentUser}>
          <Agreement />
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/preview-invoice/:invoiceId',
      element: (
        <RootLayout currentUser={currentUser}>
          <PreviewInvoice />
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/edit-invoice/:invoiceId',
      element: (
        <RootLayout currentUser={currentUser}>
          <ProtectedRoute currentUser={currentUser}>
            <EditInvoice />
          </ProtectedRoute>
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/payment/:invoiceId',
      element: (
        <RootLayout currentUser={currentUser}>
          <PaymentPage />
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/payment-confirmation/:invoiceId',
      element: (
        <RootLayout currentUser={currentUser}>
          <PaymentConfirmation />
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/survey-response/:surveyId',
      element: (
        <RootLayout currentUser={currentUser}>
          <SurveyResponse />
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/survey/:surveyId/responses',
      element: (
        <RootLayout currentUser={currentUser}>
          <ProtectedRoute currentUser={currentUser}>
            <SurveyResponses />
          </ProtectedRoute>
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/survey-thank-you',
      element: (
        <RootLayout currentUser={currentUser}>
          <SurveyThankYou />
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/success',
      element: (
        <RootLayout currentUser={currentUser}>
          <SubscriptionSuccess />
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/cancel',
      element: (
        <RootLayout currentUser={currentUser}>
          <SubscriptionPage />
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '/gmail/callback',
      element: (
        <RootLayout currentUser={currentUser}>
          <GmailCallback />
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    },
    {
      path: '*',
      element: (
        <RootLayout currentUser={currentUser}>
          {currentUser ? (
            sessionStorage.getItem('isNewUser') === 'true' ? 
            <Navigate to="/app/onboarding" /> : 
            <Navigate to="/app" />
          ) : <Navigate to="/" />}
        </RootLayout>
      ),
      errorElement: <ErrorBoundary />
    }
  ]);

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen text-white">Loading...</div>;
  }

  return <RouterProvider router={router} />;
}

export default App;